import React from 'react';
import PrimaryTagsSelector from './PrimaryTagsSelector';
import SecondaryTagsInput from './SecondaryTagsInput';
import SongArtworkUpload from './SongArtworkUpload';

interface SongEditorSongTabProps {
  songData: any;
  songId?: string;
  primaryTagIds: string[];
  secondaryTagNames?: string[];
  onUpdate: (updates: any) => void;
  onPrimaryTagsChange: (tagIds: string[]) => void;
  onSecondaryTagsChange?: (tagNames: string[]) => void;
}

export default function SongEditorSongTab({
  songData,
  songId,
  primaryTagIds,
  secondaryTagNames,
  onUpdate,
  onPrimaryTagsChange,
  onSecondaryTagsChange
}: SongEditorSongTabProps) {
  return (
    <div className="space-y-5 md:space-y-6">
      {/* Basic Information with Artwork */}
      <section className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 md:p-6 shadow-sm md:shadow-md">
        <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4 md:mb-3">Basic Information</h3>

        {/* Mobile: Artwork first, Desktop: Side-by-side */}
        <div className="flex flex-col lg:grid lg:grid-cols-[minmax(480px,1fr)_300px] gap-6">
          {/* Artwork - Shows first on mobile, right on desktop */}
          <div className="flex-shrink-0 lg:order-2">
            <SongArtworkUpload
              currentImageUrl={songData.image_url}
              songId={songId}
              onUploadComplete={(url) => onUpdate({ image_url: url })}
              onRemove={() => onUpdate({ image_url: null })}
            />
          </div>

          {/* Fields - Shows second on mobile, left on desktop */}
          <div className="space-y-5 min-w-0 lg:order-1">
            {/* Title & Artist stacked on mobile, side-by-side on tablet+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4">
              {/* Title */}
              <div>
                <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                  Song Title <span className="text-[#FF6B6B]">*</span>
                </label>
                <input
                  type="text"
                  value={songData.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
                  placeholder="e.g., Born in the U.S.A."
                />
              </div>

              {/* Artist */}
              <div>
                <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                  Artist <span className="text-[#FF6B6B]">*</span>
                </label>
                <input
                  type="text"
                  value={songData.artist || ''}
                  onChange={(e) => onUpdate({ artist: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
                  placeholder="e.g., Bruce Springsteen"
                />
              </div>
            </div>

            {/* Explore Card Caption */}
            <div>
              <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                Explore Card Caption
              </label>
              <textarea
                value={songData.description || ''}
                onChange={(e) => onUpdate({ description: e.target.value })}
                maxLength={100}
                rows={2}
                className="w-full h-[128px] px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] resize-none focus:outline-none focus:border-[#40E0D0]/50"
                placeholder="Short description shown on Explore cards (1-2 sentences)"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-[#606060]">
                  {(songData.description || '').length}/100 characters
                </span>
                {(songData.description || '').length === 100 && (
                  <span className="text-[11px] text-[#FF6B6B]">
                    Character limit reached.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Classification */}
      <section className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 md:p-6 space-y-5 shadow-sm md:shadow-md">
        <h3 className="text-[16px] font-semibold text-[#F5F5F5]">Classification</h3>

        {/* Primary Tags (User-facing Explore filters) */}
        <PrimaryTagsSelector
          selectedTagIds={primaryTagIds}
          onChange={onPrimaryTagsChange}
          maxSelection={2}
        />

        {/* Secondary Tags (Internal admin-only) */}
        <SecondaryTagsInput
          value={secondaryTagNames}
          onChange={onSecondaryTagsChange}
        />
      </section>

      {/* Streaming Links */}
      <section className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 md:p-6 space-y-5 shadow-sm md:shadow-md">
        <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-2">Streaming Links</h3>

        {/* Spotify URL */}
        <div>
          <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
            Spotify URL
          </label>
          <input
            type="url"
            value={songData.spotify_url || ''}
            onChange={(e) => onUpdate({ spotify_url: e.target.value })}
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
            placeholder="https://open.spotify.com/track/..."
          />
        </div>

        {/* Apple Music URL */}
        <div>
          <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
            Apple Music URL
          </label>
          <input
            type="url"
            value={songData.apple_music_url || ''}
            onChange={(e) => onUpdate({ apple_music_url: e.target.value })}
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
            placeholder="https://music.apple.com/..."
          />
        </div>

        {/* YouTube URL */}
        <div>
          <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
            YouTube URL
          </label>
          <input
            type="url"
            value={songData.youtube_url || ''}
            onChange={(e) => onUpdate({ youtube_url: e.target.value })}
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
            placeholder="https://youtube.com/watch?v=..."
          />
        </div>
      </section>
    </div>
  );
}
